import { LAYER_REGISTRY } from "../map/registry";
import { GROUP_LABELS, type LayerGroup, type LegendContext } from "../map/types";
import { useLayerStore } from "../state/useLayerStore";
import { Legend } from "./Legend";
import { AddressSearch } from "./AddressSearch";
import { Shortlist } from "./Shortlist";
import { SectionTitle } from "./ui/Field";
import { TransitViewControl } from "./TransitViewControl";
import { useDemographicsStore } from "../state/useDemographicsStore";
import { useRisksStore } from "../state/useRisksStore";
import { useClimateStore } from "../state/useClimateStore";

const GROUP_ORDER: LayerGroup[] = [
  "prices",
  "people",
  "risks",
  "climate",
  "environment",
  "transit",
];

/** The Explore pane: find a place and pick the layers. */
export function LayerPanel() {
  const visibility = useLayerStore((s) => s.visibility);
  const toggle = useLayerStore((s) => s.toggle);
  const unavailable = useLayerStore((s) => s.unavailable);
  const transitView = useLayerStore((s) => s.transitView);
  const highlightedLines = useLayerStore((s) => s.highlightedLines);
  const toggleLine = useLayerStore((s) => s.toggleLine);
  const toggleLines = useLayerStore((s) => s.toggleLines);
  const clearLines = useLayerStore((s) => s.clearLines);

  // Several layers are shaded by whichever measure their pane is set to, so
  // their legends have to follow those controls rather than sit in the registry
  // as constants. Gathered here, unconditionally, and handed to whichever
  // definitions asked for it — a `legendFor` cannot read a store itself without
  // calling a hook inside a loop.
  const ctx: LegendContext = {
    transitView,
    demographicsMetric: useDemographicsStore((s) => s.metric),
    demographicsPeriod: useDemographicsStore((s) => s.period),
    riskMetric: useRisksStore((s) => s.metric),
    riskRecentOnly: useRisksStore((s) => s.recentOnly),
    climateMetric: useClimateStore((s) => s.metric),
    climateScenario: useClimateStore((s) => s.scenario),
    climateHorizon: useClimateStore((s) => s.horizon),
    heatWhen: useClimateStore((s) => s.heatWhen),
  };

  const groups = GROUP_ORDER.map((g) => ({
    group: g,
    layers: LAYER_REGISTRY.filter((l) => l.group === g),
  })).filter((g) => g.layers.length > 0);

  return (
    <div className="px-4 py-3">
      <AddressSearch />

      <div className="mt-3">
        {groups.length === 0 ? (
          <p className="text-xs text-ink-soft">
            No data layers built yet.
          </p>
        ) : (
          groups.map(({ group, layers }) => (
            <section key={group} className="mb-4 last:mb-0">
              <SectionTitle>{GROUP_LABELS[group]}</SectionTitle>
              <ul className="space-y-1.5">
                {layers.map((def) => {
                  const missing = unavailable.includes(def.id);
                  const on = !missing && (visibility[def.id] ?? def.defaultVisible);
                  return (
                    <li key={def.id} className={missing ? "opacity-50" : undefined}>
                      {/* The description is a tooltip rather than a line of
                          its own: it is read once, and it was costing more
                          vertical space than the whole layer list. */}
                      <label
                        title={missing ? "Tiles not built" : def.description}
                        className={`flex items-center gap-2.5 ${
                          missing ? "cursor-not-allowed" : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={missing}
                          onChange={() => toggle(def.id)}
                          className="h-4 w-4 shrink-0 accent-accent"
                        />
                        <span className="min-w-0 truncate text-[13px] font-medium leading-tight">
                          {def.label}
                        </span>
                      </label>
                      {/* Transit is the one layer with two renderings of
                          the same data, so it needs a control of its own on
                          top of the legend the registry gives it. */}
                      {on && def.id === "transit" && <TransitViewControl />}
                      {on &&
                        (() => {
                          const legend = def.legendFor?.(ctx) ?? def.legend;
                          if (!legend) return null;
                          // Only the transit lines view has a chip per line to
                          // pick; everywhere else the legend is read, not
                          // clicked.
                          const pickable = def.id === "transit" && transitView === "lines";
                          return (
                            <Legend
                              legend={legend}
                              selected={pickable ? highlightedLines : undefined}
                              onSelect={pickable ? toggleLine : undefined}
                              onSelectMany={pickable ? toggleLines : undefined}
                              onClear={pickable ? clearLines : undefined}
                            />
                          );
                        })()}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
      </div>
      <Shortlist />
    </div>
  );
}
